import type { FunctionComponent } from "react";
import { useMemo } from "react";
import { graphql, useFragment } from "react-relay";

import type { Props as ArticleCommentsTriggerProps } from "~/components/article/article-comments-trigger";
import { ArticleCommentsTrigger } from "~/components/article/article-comments-trigger";
import type { articleActionWidgetArticle$key } from "~/queries/__generated__/articleActionWidgetArticle.graphql";

import type { ContentBookmarkProps } from "./styles";
import { Container, StyledContentBookmark } from "./styles";

type Variant = "default" | "photoEssay";

enum ActionType {
  bookmark = "bookmark",
  comment = "comment",
}

type OptionsMap = Record<
  Variant,
  {
    types: ActionType[];
  }
>;

const optionsMap: OptionsMap = {
  default: {
    types: [ActionType.bookmark, ActionType.comment],
  },
  photoEssay: {
    types: [ActionType.bookmark, ActionType.comment],
  },
};

type Props = {
  className?: string;
  reference: articleActionWidgetArticle$key;
  variant?: Variant;
};

export const ArticleActionWidget: FunctionComponent<Props> = ({
  className,
  reference: reference_,
  variant = "default",
}) => {
  const article = useFragment(
    graphql`
      fragment articleActionWidgetArticle on Article {
        ...articleCommentsTriggerArticle
        entityId
        urlAlias
      }
    `,
    reference_,
  );

  const commentWidgetVariant = useMemo<ArticleCommentsTriggerProps["variant"]>(() => {
    switch (variant) {
      case "photoEssay":
        return "widgetDark";
      default:
        return "widgetLight";
    }
  }, [variant]);

  const bookmarkWidgetVariant = useMemo<ContentBookmarkProps["$variant"]>(() => {
    switch (variant) {
      case "photoEssay":
        return "dark";
      default:
        return "light";
    }
  }, [variant]);

  return (
    <Container className={className}>
      {optionsMap[variant].types.includes(ActionType.comment) && (
        <ArticleCommentsTrigger
          location="Article Side"
          reference={article}
          variant={commentWidgetVariant}
        />
      )}
      {optionsMap[variant].types.includes(ActionType.bookmark) && (
        <StyledContentBookmark
          $variant={bookmarkWidgetVariant}
          entityId={article.entityId}
          location="side"
          urlAlias={article.urlAlias}
        />
      )}
    </Container>
  );
};
ArticleActionWidget.displayName = "ArticleActionWidget";
